import React, { useEffect, useRef, useContext, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Label from "./LabelF";
import {
  validarRegex,
  getAtributo,
  requestErrorHandler,
} from "../../../context/FuncionesContext";
import { useFiles } from "../../context/FileContext";
import ABMContext from "../../context/ABMContext";
import Nullifier from "../../../components/Nullifier";
import {
  startCameraScanner,
  waitForVideoElement,
} from "../../../../../helpers/scanner";

const InputAbmEscaner = (props) => {
  const { setValor, valor, cab, error, setError, data } = props;
  const { submit, opciones, modalProps, valorFormulario } =
    useContext(ABMContext);
  const { files } = useFiles();

  const {
    id,
    id_a,
    margin_bottom_abm,
    permite_null,
    width,
    maximo_caracteres,
    min_width,
    input_size,
  } = cab;

  const attemptSubmit = useRef(null);
  const videoRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerActiveRef = useRef(false);
  const scannerLockRef = useRef(false);
  const scannerOpenedByFocusRef = useRef(false);
  const pendingInternalValueRef = useRef(null);
  const previousValorRef = useRef(valor);
  const [inputValue, setInputValue] = useState(valor ?? "");
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");

  const isMobileDevice = (() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }

    const userAgent =
      navigator.userAgent || navigator.vendor || window.opera || "";

    return (
      /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent) ||
      window.matchMedia?.("(max-width: 900px)").matches
    );
  })();

  const supportsCamera =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    const mydefault = data[id_a] ?? data[id_a + "_default"] ?? cab.default;
    if (!valor) {
      pendingInternalValueRef.current = mydefault;
      setValor(mydefault);
      setInputValue(mydefault ?? "");
    }
  }, []);

  useEffect(() => {
    return () => {
      if (attemptSubmit.current) {
        clearTimeout(attemptSubmit.current);
      }
      if (scannerControlsRef.current) {
        scannerControlsRef.current.stop();
      }
      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const crearFormData = (formValues) => {
    const formData = new FormData();
    Object.keys(formValues).forEach((key) => {
      formData.append(key, formValues[key]);
    });
    files.forEach((archivo, index) => {
      formData.append(`${archivo.id_a}#${index}`, archivo.file);
    });
    return formData;
  };

  const validarValor = (value) => {
    const max =
      cab.valor_maximo && cab.valor_maximo !== ""
        ? Number(cab.valor_maximo)
        : undefined;
    const min =
      cab.valor_minimo && cab.valor_minimo !== ""
        ? Number(cab.valor_minimo)
        : undefined;
    if (max && Number(value) > max) return false;
    if (min && Number(value) < min) return false;
    const regexp = getAtributo("validacion_regex", data, cab) ?? cab.validacion_regex;

    return validarRegex(value, regexp, cab);
  };

  const cerrarModal = () => {
    if (opciones?.cerrar_modal === "s" && modalProps) {
      modalProps.cerrar();
    }
  };

  const forzarSubmit =
    (getAtributo("forzar_submit", data, cab) ?? cab.forzar_submit ?? "s") ===
    "s";
  const formatos = getAtributo("formatos", data, cab);

  const submitAbm = (value) => {
    if (!submit) return;

    const formValues = {
      ...valorFormulario,
      [id_a]: value,
      id_a: opciones.id_a,
      _escaneado: true,
    };

    if (cab.clave_envio && cab.clave_envio.trim() !== "") {
      formValues[cab.clave_envio] = value;
    }

    const params = crearFormData(formValues);

    submit({
      params,
      funcion: "InputAbmEscaner submit",
      cab: opciones,
    })
      .then((res) => {
        if (res?.code === "cancelado") return;
        if (res?.status >= 400) return requestErrorHandler(res);
        cerrarModal();
      })
      .catch((err) => {
        console.log("InputAbmEscaner submit error", err);
      });
  };

  useEffect(() => {
    const previousValor = previousValorRef.current;
    previousValorRef.current = valor;
    setInputValue(valor ?? "");

    if (Object.is(previousValor, valor)) return;

    if (Object.is(pendingInternalValueRef.current, valor)) {
      pendingInternalValueRef.current = null;
      return;
    }
    pendingInternalValueRef.current = null;

    const externalValue = String(valor ?? "").trim();
    if (!externalValue) return;

    if (!validarValor(externalValue)) {
      setError((currentErrors) => ({
        ...currentErrors,
        [id_a]: true,
      }));
      return;
    }

    setError((currentErrors) => ({
      ...currentErrors,
      [id_a]: false,
    }));

    if (forzarSubmit) {
      submitAbm(externalValue);
    }
  }, [valor]);

  const stopScanner = () => {
    scannerActiveRef.current = false;

    if (scannerControlsRef.current) {
      scannerControlsRef.current.stop();
      scannerControlsRef.current = null;
    }

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    scannerLockRef.current = false;
    setIsScannerOpen(false);
    setIsProcessingScan(false);
    setCameras([]);
    setSelectedCamera("");
  };

  const applyScannedValue = (rawValue) => {
    let value = String(rawValue ?? "").trim();
    if (!value) return;

    if (cab.solo_mayus === "s") {
      value = value.toUpperCase();
    }

    if (maximo_caracteres && value.length > maximo_caracteres) {
      value = value.slice(0, maximo_caracteres);
    }

    setInputValue(value);
    setError((e) => {
      return { ...e, [id_a]: false };
    });

    if (!validarValor(value)) {
      setError((e) => {
        return { ...e, [id_a]: true };
      });
      return;
    }

    pendingInternalValueRef.current = value;
    setValor(value);

    if (forzarSubmit) {
      submitAbm(value);
    }
  };

  const handleDetectedCode = (rawValue) => {
    if (!rawValue || scannerLockRef.current) return;

    scannerLockRef.current = true;
    applyScannedValue(rawValue);
    stopScanner();
  };

  const openMobileScanner = async () => {
    setScannerError("");

    if (!isMobileDevice) {
      return;
    }

    if (!supportsCamera) {
      setScannerError("El navegador no permite acceder a la camara en vivo.");
      return;
    }

    try {
      scannerActiveRef.current = true;
      setIsScannerOpen(true);
      setIsProcessingScan(true);

      const video = await waitForVideoElement(videoRef);
      const controls = await startCameraScanner("multi-format", {
        video,
        onDetected: handleDetectedCode,
        formatos,
      });

      if (!scannerActiveRef.current || scannerLockRef.current) {
        controls.stop();
        return;
      }

      scannerControlsRef.current = controls;
      scannerStreamRef.current = controls.getStream?.() ?? null;
      const availableCameras = await controls.listCameras?.();
      const activeDeviceId = scannerStreamRef.current
        ?.getVideoTracks?.()[0]
        ?.getSettings?.().deviceId;
      setCameras(availableCameras ?? []);
      setSelectedCamera(activeDeviceId || "");
      setIsProcessingScan(false);
    } catch (error) {
      setScannerError("No se pudo iniciar el lector de codigos.");
      stopScanner();
    }
  };

  const handleFocus = () => {
    const abrirCamaraOnFocus =
      getAtributo("abrir_camara_onFocus", data, cab) === "s";

    if (
      !abrirCamaraOnFocus ||
      !isMobileDevice ||
      scannerOpenedByFocusRef.current ||
      isScannerOpen
    ) {
      return;
    }

    scannerOpenedByFocusRef.current = true;
    openMobileScanner();
  };

  const handleBlur = () => {
    scannerOpenedByFocusRef.current = false;
  };

  const handleCameraChange = async (event) => {
    const cameraId = event.target.value;
    setSelectedCamera(cameraId);
    setIsProcessingScan(true);

    try {
      await scannerControlsRef.current?.setCamera?.(cameraId);
      setScannerError("");
    } catch (error) {
      setScannerError("No se pudo cambiar a esa camara.");
    } finally {
      setIsProcessingScan(false);
    }
  };

  const handleInput = (e) => {
    let { value } = e.target;

    if (cab.solo_mayus === "s") {
      value = value.toUpperCase();
    }
    if (maximo_caracteres && value.length > maximo_caracteres) return;

    setInputValue(value);
    setError((e) => {
      return { ...e, [id_a]: false };
    });

    if (!forzarSubmit) {
      if (validarValor(value)) {
        pendingInternalValueRef.current = value;
        setValor(value);
      }
      return;
    }

    if (attemptSubmit.current) {
      clearTimeout(attemptSubmit.current);
      attemptSubmit.current = null;
    }

    attemptSubmit.current = setTimeout(() => {
      if (!validarValor(value)) {
        setError((e) => {
          return { ...e, [id_a]: true };
        });
        return;
      }
      pendingInternalValueRef.current = value;
      setValor(value);
      submitAbm(value);
    }, 100);
  };

  const handleNull = () => {
    setInputValue("null");
    pendingInternalValueRef.current = "null";
    setValor("null");
    setError((e) => {
      return { ...e, [id_a]: false };
    });
  };

  const style_input_abm = {
    marginBottom: margin_bottom_abm,
    width: input_size || width || "100%",
    minWidth: input_size || min_width,
  };

  return (
    <>
      {cab.nombre ? (
        <Label
          label={cab.nombre}
          opcionales_null={cab.opcionales_null}
          permite_null={cab.permite_null}
        />
      ) : (
        <></>
      )}
      <TextField
        id={id}
        type="text"
        placeholder={cab.placeholder}
        onChange={handleInput}
        helperText={
          error[id_a]
            ? cab.helper_text || cab.helperText || "Formato invalido"
            : ""
        }
        label={cab.label}
        value={inputValue ?? ""}
        error={error[id_a]}
        variant="outlined"
        size="small"
        className={cab.input_class_caja}
        inputProps={{
          "data-input-configuracion-id": cab.id_a,
          "data-input-clave-envio": cab.clave_envio || undefined,
          "data-input-campo-alias": cab.campo_alias || undefined,
          style: {
            textTransform: cab.solo_mayus === "s" ? "uppercase" : "inherit",
          },
          maxLength: cab.maximo_caracteres,
          className:
            cab.input_class_text +
            " textfield_input_text_number input-text-escaner",
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={style_input_abm}
        disabled={
          data[cab.id_a + "_disabled"]
            ? data[cab.id_a + "_disabled"] === "s"
            : cab.disabled === "s"
        }
      />
      {scannerError ? (
        <div
          style={{
            marginTop: "6px",
            color: "#b42318",
            fontSize: "0.8rem",
          }}
        >
          {scannerError}
        </div>
      ) : null}
      {isScannerOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 1600,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            gap: "12px",
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              background: "#000",
            }}
          />
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            {isProcessingScan
              ? "Abriendo camara..."
              : "Apunta la camara al codigo o QR"}
          </div>
          {cameras.length > 1 ? (
            <select
              value={selectedCamera}
              onChange={handleCameraChange}
              aria-label="Seleccionar camara"
              style={{
                width: "100%",
                maxWidth: "420px",
                padding: "10px 12px",
                borderRadius: "8px",
                background: "#fff",
              }}
            >
              <option value="" disabled>
                Seleccionar camara
              </option>
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.label}
                </option>
              ))}
            </select>
          ) : null}
          <Button
            variant="contained"
            onClick={stopScanner}
            style={{
              background: "#fff",
              color: "#111",
              position: "absolute",
              top: "16px",
              right: "16px",
              zIndex: 1,
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : null}
      {permite_null === "s" ? (
        <Nullifier onClick={handleNull} className="columna_input_null" />
      ) : (
        <></>
      )}
    </>
  );
};

export default InputAbmEscaner;
